import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  TrendingUp, 
  FileText, 
  Image, 
  Link2, 
  Search,
  RefreshCw,
  Loader2,
  ExternalLink,
  CheckSquare,
  Square,
  Edit3,
  Sparkles,
  Twitter,
  Facebook,
  ArrowRight,
  Copy,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://primestatlab.com';

export default function SEODashboard() {
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [seoData, setSeoData] = useState(null);
  const [articlesNeedingWork, setArticlesNeedingWork] = useState({ titles: [], short: [] });
  const [showTitleFixer, setShowTitleFixer] = useState(false);
  const [titleToFix, setTitleToFix] = useState('');
  const [suggestedTitles, setSuggestedTitles] = useState([]);
  const [generatingTitles, setGeneratingTitles] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ titles: false, short: false });
  
  const [dailyChecklist, setDailyChecklist] = useState(() => {
    const saved = localStorage.getItem('seoChecklist');
    const today = new Date().toDateString();
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.date === today) return parsed.items;
    }
    return {
      postArticle: false,
      shareTwitter: false,
      shareFacebook: false,
      addPick: false,
      checkAnalytics: false
    };
  });

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      analyzeSite();
    }
  }, [authLoading, isAuthenticated]);

  // Save checklist to localStorage
  useEffect(() => {
    localStorage.setItem('seoChecklist', JSON.stringify({
      date: new Date().toDateString(),
      items: dailyChecklist
    }));
  }, [dailyChecklist]);

  const analyzeSite = async () => {
    setAnalyzing(true);
    try {
      // Fetch articles
      const articlesRes = await axios.get(`${API}/articles`);
      const articles = articlesRes.data;

      // Fetch picks
      const picksRes = await axios.get(`${API}/picks`);
      const picks = picksRes.data;

      // Analyze SEO
      const issues = [];
      const warnings = [];
      const successes = [];

      // Track specific articles needing work
      const titleIssues = [];
      const shortArticles = [];

      // Check articles
      let articlesWithGoodTitles = 0;
      let articlesWithShortContent = 0;
      let articlesThisWeek = 0;
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

      articles.forEach(article => {
        // Title length (ideal: 50-60 chars)
        const titleLength = article.title ? article.title.length : 0;
        if (titleLength >= 40 && titleLength <= 65) {
          articlesWithGoodTitles++;
        } else {
          titleIssues.push({
            id: article.id,
            slug: article.slug,
            title: article.title,
            length: titleLength,
            issue: titleLength < 40 ? 'Too short' : 'Too long',
            suggestion: titleLength < 40 ? 'Add more descriptive keywords' : 'Shorten to 50-60 characters'
          });
        }

        // Content length (ideal: 800+ words)
        const wordCount = article.content ? article.content.split(/\s+/).length : 0;
        if (wordCount < 800) {
          articlesWithShortContent++;
          shortArticles.push({
            id: article.id,
            slug: article.slug,
            title: article.title,
            wordCount,
            needed: 800 - wordCount
          });
        }

        // Recent articles
        if (new Date(article.created_at) > oneWeekAgo) {
          articlesThisWeek++;
        }
      });

      // Store articles needing work
      setArticlesNeedingWork({ titles: titleIssues, short: shortArticles });

      // Generate insights
      if (articles.length === 0) {
        issues.push({ text: 'No articles published yet - content is key for SEO', type: 'critical' });
      } else {
        if (articlesThisWeek === 0) {
          warnings.push({ text: 'No new articles this week - try to post 2-3 times per week', type: 'warning' });
        } else if (articlesThisWeek >= 3) {
          successes.push({ text: `Great posting frequency! ${articlesThisWeek} articles this week`, type: 'success' });
        } else {
          successes.push({ text: `${articlesThisWeek} article(s) posted this week`, type: 'success' });
        }

        if (articlesWithShortContent > 0) {
          warnings.push({ 
            text: `${articlesWithShortContent} article(s) under 800 words - longer content ranks better`, 
            type: 'warning',
            actionable: true,
            action: 'showShort'
          });
        }

        const titlePercentage = Math.round((articlesWithGoodTitles / articles.length) * 100);
        if (titlePercentage >= 80) {
          successes.push({ text: `${titlePercentage}% of titles are optimal length (50-60 chars)`, type: 'success' });
        } else if (titlePercentage >= 50) {
          warnings.push({ 
            text: `Only ${titlePercentage}% of titles are optimal length - aim for 50-60 characters`, 
            type: 'warning',
            actionable: true,
            action: 'showTitles'
          });
        } else {
          issues.push({ 
            text: `Most titles need work - ${titlePercentage}% are optimal length`, 
            type: 'critical',
            actionable: true,
            action: 'showTitles'
          });
        }
      }

      // Check picks (content freshness)
      const recentPicks = picks.filter(p => new Date(p.created_at) > oneWeekAgo);
      if (recentPicks.length >= 5) {
        successes.push({ text: `Active pick posting - ${recentPicks.length} picks this week`, type: 'success' });
      } else if (recentPicks.length > 0) {
        warnings.push({ text: `${recentPicks.length} picks this week - more activity helps SEO`, type: 'warning' });
      }

      // Calculate overall score
      const totalChecks = issues.length + warnings.length + successes.length;
      const score = totalChecks > 0 
        ? Math.round((successes.length / totalChecks) * 100)
        : 50;

      setSeoData({
        score,
        issues,
        warnings,
        successes,
        stats: {
          totalArticles: articles.length,
          articlesThisWeek,
          totalPicks: picks.length,
          recentPicks: recentPicks.length
        }
      });
    } catch (error) {
      console.error('SEO analysis failed:', error);
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  const toggleChecklist = (item) => {
    setDailyChecklist(prev => ({ ...prev, [item]: !prev[item] }));
  };

  // Actionable checklist handlers
  const handleChecklistAction = (action) => {
    switch(action) {
      case 'postArticle':
        window.location.href = '/admin/command-center';
        break;
      case 'shareTwitter':
        // Get latest article and share to Twitter
        if (seoData?.stats?.totalArticles > 0) {
          axios.get(`${API}/articles`).then(res => {
            if (res.data.length > 0) {
              const article = res.data[0];
              const url = `${SITE_URL}/article/${article.slug}`;
              const text = encodeURIComponent(article.title);
              window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, '_blank');
              toggleChecklist('shareTwitter');
            }
          });
        } else {
          toast.error('No articles to share yet');
        }
        break;
      case 'shareFacebook':
        if (seoData?.stats?.totalArticles > 0) {
          axios.get(`${API}/articles`).then(res => {
            if (res.data.length > 0) {
              const article = res.data[0];
              const url = `${SITE_URL}/article/${article.slug}`;
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
              toggleChecklist('shareFacebook');
            }
          });
        } else {
          toast.error('No articles to share yet');
        }
        break;
      case 'addPick':
        window.location.href = '/admin/dashboard';
        break;
      case 'checkAnalytics':
        window.open('https://analytics.google.com', '_blank');
        toggleChecklist('checkAnalytics');
        break;
      default:
        break;
    }
  };

  // Generate better title suggestions
  const generateTitleSuggestions = async () => {
    if (!titleToFix.trim()) {
      toast.error('Please enter a title to improve');
      return;
    }
    
    setGeneratingTitles(true);
    try {
      const response = await axios.post(
        `${API}/command-center/improve-title`,
        { title: titleToFix },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.suggestions) {
        setSuggestedTitles(response.data.suggestions);
        toast.success('Title suggestions generated!');
      }
    } catch (error) {
      // If endpoint doesn't exist, use a simple local suggestion
      const suggestions = [
        `${titleToFix} - Expert Analysis & Predictions`,
        `Breaking Down ${titleToFix}: What You Need to Know`,
        `${titleToFix} | PrimeStatLab Insider Report`
      ].filter(s => s.length >= 40 && s.length <= 65);
      
      if (suggestions.length === 0) {
        // Truncate or expand as needed
        const baseTitle = titleToFix.substring(0, 45);
        setSuggestedTitles([
          `${baseTitle} - Analysis`,
          `${baseTitle} - Breakdown`,
          `${baseTitle} - Expert Picks`
        ]);
      } else {
        setSuggestedTitles(suggestions);
      }
      toast.success('Title suggestions generated!');
    } finally {
      setGeneratingTitles(false);
    }
  };

  const copyTitle = (title) => {
    navigator.clipboard.writeText(title);
    toast.success('Title copied to clipboard!');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-accent';
    if (score >= 50) return 'text-yellow-500';
    return 'text-loss';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Critical';
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="seo-dashboard">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-2">SEO DASHBOARD</h1>
            <p className="text-muted-foreground">Monitor and improve your site's search visibility</p>
          </div>
          <Button
            onClick={analyzeSite}
            disabled={analyzing}
            className="bg-primary text-white"
          >
            {analyzing ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2" size={16} />
                Re-Analyze
              </>
            )}
          </Button>
        </div>

        {seoData && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Score & Issues */}
            <div className="lg:col-span-2 space-y-6">
              {/* Score Card */}
              <Card className="bg-card border-border">
                <CardContent className="py-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                        Overall SEO Health
                      </p>
                      <div className="flex items-baseline gap-3">
                        <span className={`text-6xl font-heading font-bold ${getScoreColor(seoData.score)}`}>
                          {seoData.score}
                        </span>
                        <span className="text-2xl text-muted-foreground">/100</span>
                      </div>
                      <p className={`text-lg font-bold mt-2 ${getScoreColor(seoData.score)}`}>
                        {getScoreLabel(seoData.score)}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6 text-center">
                      <div>
                        <p className="text-3xl font-bold text-primary">{seoData.stats.totalArticles}</p>
                        <p className="text-xs font-mono text-muted-foreground">Total Articles</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-accent">{seoData.stats.articlesThisWeek}</p>
                        <p className="text-xs font-mono text-muted-foreground">This Week</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-primary">{seoData.stats.totalPicks}</p>
                        <p className="text-xs font-mono text-muted-foreground">Total Picks</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-accent">{seoData.stats.recentPicks}</p>
                        <p className="text-xs font-mono text-muted-foreground">Picks This Week</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issues & Warnings */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading">SEO Insights</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {seoData.issues.map((issue, i) => (
                    <div key={`issue-${i}`} className="flex items-start gap-3 p-3 bg-loss/10 rounded-lg border border-loss/20">
                      <XCircle className="text-loss shrink-0 mt-0.5" size={18} />
                      <div className="flex-1">
                        <p className="text-sm">{issue.text}</p>
                        {issue.actionable && (
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-loss p-0 h-auto mt-1"
                            onClick={() => setExpandedSections(prev => ({ ...prev, titles: true }))}
                          >
                            View articles that need work <ArrowRight size={14} className="ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {seoData.warnings.map((warning, i) => (
                    <div key={`warning-${i}`} className="flex items-start gap-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <AlertTriangle className="text-yellow-500 shrink-0 mt-0.5" size={18} />
                      <div className="flex-1">
                        <p className="text-sm">{warning.text}</p>
                        {warning.actionable && (
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-yellow-500 p-0 h-auto mt-1"
                            onClick={() => setExpandedSections(prev => ({ 
                              ...prev, 
                              [warning.action === 'showTitles' ? 'titles' : 'short']: true 
                            }))}
                          >
                            View specific articles <ArrowRight size={14} className="ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {seoData.successes.map((success, i) => (
                    <div key={`success-${i}`} className="flex items-center gap-3 p-3 bg-accent/10 rounded-lg border border-accent/20">
                      <CheckCircle2 className="text-accent shrink-0" size={18} />
                      <p className="text-sm">{success.text}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Articles Needing Title Work */}
              {articlesNeedingWork.titles.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader 
                    className="cursor-pointer"
                    onClick={() => setExpandedSections(prev => ({ ...prev, titles: !prev.titles }))}
                  >
                    <CardTitle className="font-heading flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Edit3 size={20} className="text-yellow-500" />
                        Articles with Title Issues ({articlesNeedingWork.titles.length})
                      </span>
                      {expandedSections.titles ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </CardTitle>
                  </CardHeader>
                  {expandedSections.titles && (
                    <CardContent className="space-y-3">
                      {articlesNeedingWork.titles.map((article, i) => (
                        <div key={i} className="p-4 bg-surface rounded-lg border border-border">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="font-medium text-sm mb-1">{article.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {article.length} chars ({article.issue}) - {article.suggestion}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTitleToFix(article.title);
                                  setShowTitleFixer(true);
                                  setSuggestedTitles([]);
                                }}
                              >
                                <Sparkles size={14} className="mr-1" />
                                Improve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`/article/${article.slug}`, '_blank')}
                              >
                                <ExternalLink size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Short Articles */}
              {articlesNeedingWork.short.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader 
                    className="cursor-pointer"
                    onClick={() => setExpandedSections(prev => ({ ...prev, short: !prev.short }))}
                  >
                    <CardTitle className="font-heading flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText size={20} className="text-yellow-500" />
                        Articles Under 800 Words ({articlesNeedingWork.short.length})
                      </span>
                      {expandedSections.short ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </CardTitle>
                  </CardHeader>
                  {expandedSections.short && (
                    <CardContent className="space-y-3">
                      {articlesNeedingWork.short.map((article, i) => (
                        <div key={i} className="p-4 bg-surface rounded-lg border border-border flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm mb-1">{article.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {article.wordCount} words (need {article.needed} more)
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/article/${article.slug}`, '_blank')}
                          >
                            Edit Article <ExternalLink size={14} className="ml-1" />
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Title Improvement Tool */}
              {showTitleFixer && (
                <Card className="bg-card border-border border-primary">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Sparkles size={20} className="text-primary" />
                      Title Improvement Tool
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-mono uppercase">Current Title</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={titleToFix}
                          onChange={(e) => setTitleToFix(e.target.value)}
                          placeholder="Enter title to improve..."
                          className="flex-1"
                        />
                        <Button
                          onClick={generateTitleSuggestions}
                          disabled={generatingTitles}
                          className="bg-primary"
                        >
                          {generatingTitles ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            <>
                              <Sparkles size={16} className="mr-1" />
                              Suggest
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Current: {titleToFix.length} chars (optimal: 50-60)
                      </p>
                    </div>

                    {suggestedTitles.length > 0 && (
                      <div>
                        <Label className="text-sm font-mono uppercase">Suggestions</Label>
                        <div className="space-y-2 mt-2">
                          {suggestedTitles.map((title, i) => (
                            <div key={i} className="flex items-center gap-2 p-3 bg-surface rounded-lg border border-border">
                              <p className="flex-1 text-sm">{title}</p>
                              <span className="text-xs text-muted-foreground">{title.length} chars</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyTitle(title)}
                              >
                                <Copy size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowTitleFixer(false);
                        setTitleToFix('');
                        setSuggestedTitles([]);
                      }}
                    >
                      Close
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Actionable Checklist */}
            <div className="space-y-6">
              <Card className="bg-card border-border sticky top-6">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <CheckSquare size={20} className="text-accent" />
                    Daily SEO Checklist
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Click items to complete them</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Post Article */}
                  <button
                    onClick={() => handleChecklistAction('postArticle')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      dailyChecklist.postArticle 
                        ? 'bg-accent/10 border-accent/30' 
                        : 'bg-surface border-border hover:border-primary/50'
                    }`}
                  >
                    {dailyChecklist.postArticle ? (
                      <CheckSquare className="text-accent shrink-0" size={20} />
                    ) : (
                      <Square className="text-muted-foreground shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">Post new article</p>
                      <p className="text-xs text-muted-foreground">Opens Command Center</p>
                    </div>
                    <ArrowRight size={16} className="text-muted-foreground" />
                  </button>

                  {/* Share to Twitter */}
                  <button
                    onClick={() => handleChecklistAction('shareTwitter')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      dailyChecklist.shareTwitter 
                        ? 'bg-accent/10 border-accent/30' 
                        : 'bg-surface border-border hover:border-[#1DA1F2]/50'
                    }`}
                  >
                    {dailyChecklist.shareTwitter ? (
                      <CheckSquare className="text-accent shrink-0" size={20} />
                    ) : (
                      <Twitter className="text-[#1DA1F2] shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">Share to Twitter/X</p>
                      <p className="text-xs text-muted-foreground">Share latest article</p>
                    </div>
                    <ExternalLink size={16} className="text-muted-foreground" />
                  </button>

                  {/* Share to Facebook */}
                  <button
                    onClick={() => handleChecklistAction('shareFacebook')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      dailyChecklist.shareFacebook 
                        ? 'bg-accent/10 border-accent/30' 
                        : 'bg-surface border-border hover:border-[#4267B2]/50'
                    }`}
                  >
                    {dailyChecklist.shareFacebook ? (
                      <CheckSquare className="text-accent shrink-0" size={20} />
                    ) : (
                      <Facebook className="text-[#4267B2] shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">Share to Facebook</p>
                      <p className="text-xs text-muted-foreground">Share latest article</p>
                    </div>
                    <ExternalLink size={16} className="text-muted-foreground" />
                  </button>

                  {/* Add Pick */}
                  <button
                    onClick={() => handleChecklistAction('addPick')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      dailyChecklist.addPick 
                        ? 'bg-accent/10 border-accent/30' 
                        : 'bg-surface border-border hover:border-primary/50'
                    }`}
                  >
                    {dailyChecklist.addPick ? (
                      <CheckSquare className="text-accent shrink-0" size={20} />
                    ) : (
                      <TrendingUp className="text-primary shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">Add today's picks</p>
                      <p className="text-xs text-muted-foreground">Opens Admin Dashboard</p>
                    </div>
                    <ArrowRight size={16} className="text-muted-foreground" />
                  </button>

                  {/* Check Analytics */}
                  <button
                    onClick={() => handleChecklistAction('checkAnalytics')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      dailyChecklist.checkAnalytics 
                        ? 'bg-accent/10 border-accent/30' 
                        : 'bg-surface border-border hover:border-primary/50'
                    }`}
                  >
                    {dailyChecklist.checkAnalytics ? (
                      <CheckSquare className="text-accent shrink-0" size={20} />
                    ) : (
                      <Search className="text-primary shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">Check Google Analytics</p>
                      <p className="text-xs text-muted-foreground">Opens analytics.google.com</p>
                    </div>
                    <ExternalLink size={16} className="text-muted-foreground" />
                  </button>

                  {/* Progress */}
                  <div className="pt-4 mt-4 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Today's Progress</span>
                      <span className="font-bold text-accent">
                        {Object.values(dailyChecklist).filter(Boolean).length}/5
                      </span>
                    </div>
                    <div className="w-full bg-surface rounded-full h-2 mt-2">
                      <div 
                        className="bg-accent h-2 rounded-full transition-all"
                        style={{ width: `${(Object.values(dailyChecklist).filter(Boolean).length / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowTitleFixer(true)}
                  >
                    <Sparkles size={16} className="mr-2 text-primary" />
                    Improve a Title
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => window.open('https://search.google.com/search-console', '_blank')}
                  >
                    <Search size={16} className="mr-2" />
                    Google Search Console
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => window.location.href = '/articles'}
                  >
                    <FileText size={16} className="mr-2" />
                    View All Articles
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
